import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPlus, FiEye, FiSearch, FiEdit2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatDate } from '../../utils/format';

export default function Invoices() {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const isPurchaseRoute = location.pathname.includes('/purchase');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, invoice_type: isPurchaseRoute ? 'purchase' : '', per_page: 100, search: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.invoices.list(params);
      const list = Array.isArray(data.data) ? data.data : data.invoices || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load invoices', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page">
      <PageHeader
        title={params.invoice_type === 'purchase' ? 'Purchase Invoices' : 'Invoices'}
        subtitle={params.invoice_type === 'purchase' ? 'Supplier purchase invoices with GST.' : 'Sale, credit, unregistered and commission invoices.'}
        crumbs={[{ label: 'Finance' }, { label: params.invoice_type === 'purchase' ? 'Purchase Invoices' : 'Invoices' }]}
        actions={hasPermission('create-invoices') && (
          <Link to={params.invoice_type === 'purchase' ? '/invoices/purchase/new' : '/invoices/new'} className="btn btn-primary"><FiPlus /> {params.invoice_type === 'purchase' ? 'New Purchase Invoice' : 'New Invoice'}</Link>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input
                placeholder="Search invoice..."
                value={params.search}
                onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
              />
            </div>
            <SearchableSelect
              options={[{ value: '', label: 'All Types' }, { value: 'sale', label: 'Sale' }, { value: 'purchase', label: 'Purchase' }, { value: 'credit', label: 'Credit Note' }, { value: 'debit', label: 'Debit Note' }, { value: 'tax_free', label: 'Unregistered' }, { value: 'commission', label: 'Commission' }]}
              value={params.invoice_type}
              onChange={(v) => setParams({ ...params, invoice_type: v, page: 1 })}
              placeholder="All Types"
            />
          </div>

          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No invoices found"
            columns={[
    { key: 'invoice_no', label: 'Invoice No', render: (r) => <b>{r.invoice_type === 'purchase' ? (r.supplier_invoice_no || r.invoice_no) : r.invoice_no}</b> },
              { key: 'invoice_type', label: 'Type', render: (r) => <StatusBadge status={r.invoice_type} /> },
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'party', label: 'Party', render: (r) => ((r.invoice_type === 'purchase' || r.invoice_type === 'debit') ? (r.supplier ? r.supplier.name : '-') : (r.party ? r.party.name : r.party_name || '-')) },
              { key: 'total', label: 'Total', align: 'right', render: (r) => formatMoney(r.total) },
              { key: 'paid', label: 'Paid', align: 'right', render: (r) => formatMoney(r.paid) },
              { key: 'remaining', label: 'Remaining', align: 'right', render: (r) => formatMoney(r.remaining) },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <Link to={`/invoices/${r.id}`} className="btn-icon" title="View"><FiEye /></Link>
                  {hasPermission('update-invoices') && (
                    <Link to={`/invoices/${r.id}/edit`} className="btn-icon" title="Edit"><FiEdit2 /></Link>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}